import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

// One task, pushed out to many companies at once. Each company ends up with an
// ordinary custom task (identical to one created from its own Task Settings page),
// but the definition is kept here so an admin can edit it once and have the change
// propagate to every company instead of repeating it company by company.

const EMPTY_TASK = {
  title: '',
  description: '',
  isRepeated: false,
  repeatDate: '',
  cycle: 'monthly',
  assignedTo: 'admin',
  taskType: 'mark-as-complete',
  fileUploadItems: [],
  dueDate: '',
  companies: [],
};

// The API stores the CSV-friendly shapes ('true'/'false', JSON strings); the form
// works with real booleans and arrays.
const toFormValues = (bulkTask) => {
  let fileUploadItems = [];
  try {
    const parsed = JSON.parse(bulkTask.fileUploadItems || '[]');
    if (Array.isArray(parsed)) fileUploadItems = parsed;
  } catch (error) {
    fileUploadItems = [];
  }
  return {
    title: bulkTask.title || '',
    description: bulkTask.description || '',
    isRepeated: bulkTask.isRepeated === 'true' || bulkTask.isRepeated === true,
    repeatDate: bulkTask.repeatDate || '',
    cycle: bulkTask.cycle || 'monthly',
    assignedTo: bulkTask.assignedTo || 'admin',
    taskType: bulkTask.taskType || 'mark-as-complete',
    fileUploadItems,
    dueDate: bulkTask.dueDate || '',
    companies: Array.isArray(bulkTask.companies) ? bulkTask.companies : [],
  };
};

const BulkTaskManagement = ({ companies = [] }) => {
  const [bulkTasks, setBulkTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // One form drives both create and edit; editingId is null while creating.
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_TASK);
  const [newFileUploadItem, setNewFileUploadItem] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  // Companies are stored and sent to the API by their internal name (that's the key
  // the task CSVs live under), but the admin should only ever see the official,
  // user-facing name shown everywhere else on the site.
  const companyOptions = companies
    .filter(c => c && c.name)
    .map(c => ({ name: c.name, label: c.officialName || c.name }));
  const companyNames = companyOptions.map(c => c.name);
  const companyLabel = (name) =>
    companyOptions.find(c => c.name === name)?.label || name;

  const fetchBulkTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/bulk`);
      if (response.ok) {
        const data = await response.json();
        setBulkTasks(data.bulkTasks || []);
      } else {
        setError('Failed to load bulk tasks');
      }
    } catch (fetchError) {
      console.error('Error fetching bulk tasks:', fetchError);
      setError('Failed to load bulk tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBulkTasks();
  }, [fetchBulkTasks]);

  const flash = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 4000);
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(EMPTY_TASK);
    setNewFileUploadItem('');
    setCompanySearch('');
    setShowForm(true);
  };

  const openEditForm = (bulkTask) => {
    setEditingId(bulkTask.id);
    setForm(toFormValues(bulkTask));
    setNewFileUploadItem('');
    setCompanySearch('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_TASK);
  };

  const toggleCompany = (name) => {
    setForm(prev => ({
      ...prev,
      companies: prev.companies.includes(name)
        ? prev.companies.filter(c => c !== name)
        : [...prev.companies, name]
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Task title is required');
      return;
    }
    if (form.companies.length === 0) {
      setError('Select at least one company');
      return;
    }

    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        fileUploadItems: JSON.stringify(form.fileUploadItems),
      };

      const response = editingId
        ? await api.put(`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/bulk/${editingId}`, payload)
        : await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/bulk`, payload);

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to save bulk task');
        return;
      }

      const data = await response.json();
      if (editingId) {
        setBulkTasks(prev => prev.map(t => (t.id === editingId ? data.bulkTask : t)));
        flash('Bulk task updated — the change was pushed to every company that has it.');
      } else {
        setBulkTasks(prev => [...prev, data.bulkTask]);
        const failed = data.failedCompanies || [];
        flash(
          failed.length > 0
            ? `Task added to ${data.bulkTask.companies.length} companies. Failed for: ${failed.map(companyLabel).join(', ')}`
            : `Task added to ${data.bulkTask.companies.length} companies.`
        );
      }
      closeForm();
    } catch (saveError) {
      console.error('Error saving bulk task:', saveError);
      setError('Failed to save bulk task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bulkTask) => {
    if (!window.confirm(
      `Remove "${bulkTask.title}" from all ${bulkTask.companies.length} companies it was sent to?`
    )) {
      return;
    }
    try {
      const response = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/bulk/${bulkTask.id}`);
      if (response.ok) {
        setBulkTasks(prev => prev.filter(t => t.id !== bulkTask.id));
        flash('Bulk task removed from every company.');
      } else {
        setError('Failed to remove bulk task');
      }
    } catch (deleteError) {
      console.error('Error deleting bulk task:', deleteError);
      setError('Failed to remove bulk task');
    }
  };

  const describeSchedule = (bulkTask) => {
    const repeated = bulkTask.isRepeated === 'true' || bulkTask.isRepeated === true;
    if (!repeated) {
      return bulkTask.dueDate ? `One-off · due ${bulkTask.dueDate}` : 'One-off';
    }
    const cycle = bulkTask.cycle === 'yearly' ? 'Yearly' : 'Monthly';
    const appears = bulkTask.repeatDate ? ` · appears ${bulkTask.repeatDate}` : '';
    const due = bulkTask.dueDate ? ` · due ${bulkTask.dueDate}` : '';
    return `${cycle}${appears}${due}`;
  };

  const visibleCompanies = companyOptions.filter(company =>
    company.label.toLowerCase().includes(companySearch.trim().toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tasks Across Companies</h3>
          <p className="text-sm text-gray-600">
            Create one task and assign it to several companies at once. It shows up in each
            company&rsquo;s Task Settings and todolist exactly like a task created there, and
            edits made here are pushed to every company that has it. Editing a company&rsquo;s
            own copy under &ldquo;By Company&rdquo; forks it: that company drops off this task
            and keeps the copy.
          </p>
        </div>
        {!showForm && (
          <button onClick={openCreateForm} className="btn-primary whitespace-nowrap">
            + New Multi-Company Task
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {showForm && (
        <div className="card p-6 space-y-4">
          <h4 className="text-md font-semibold text-gray-900">
            {editingId ? 'Edit Multi-Company Task' : 'New Multi-Company Task'}
          </h4>
          {editingId && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
              Saving will update this task for every company it was sent to. Adding a company
              creates the task there; unchecking one removes it from that company&rsquo;s list.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Task title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows="2"
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.isRepeated}
              onChange={(e) => setForm(prev => ({ ...prev, isRepeated: e.target.checked }))}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-700">Recurring task</span>
          </label>

          {form.isRepeated ? (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                <select
                  value={form.cycle}
                  onChange={(e) => setForm(prev => ({ ...prev, cycle: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.cycle === 'yearly' ? 'Appears on Date (MM-DD)' : 'Appears on Day of Month'}
                </label>
                <input
                  type="text"
                  value={form.repeatDate}
                  onChange={(e) => setForm(prev => ({ ...prev, repeatDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={form.cycle === 'yearly' ? '03-01' : '1'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.cycle === 'yearly' ? 'Due Date (MM-DD, optional)' : 'Due Day of Month (optional)'}
                </label>
                <input
                  type="text"
                  value={form.dueDate}
                  onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={form.cycle === 'yearly' ? '03-15' : '20'}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (optional)</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select
                value={form.assignedTo}
                onChange={(e) => setForm(prev => ({ ...prev, assignedTo: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="admin">Admin</option>
                <option value="client">Client</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
              <select
                value={form.taskType}
                onChange={(e) => setForm(prev => ({ ...prev, taskType: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="mark-as-complete">Mark as Complete</option>
                <option value="file-upload">File Upload</option>
              </select>
            </div>
          </div>

          {form.taskType === 'file-upload' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File Upload Items</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newFileUploadItem}
                  onChange={(e) => setNewFileUploadItem(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. Bank Statement PDF"
                />
                <button
                  onClick={() => {
                    if (newFileUploadItem.trim()) {
                      setForm(prev => ({
                        ...prev,
                        fileUploadItems: [...prev.fileUploadItems, newFileUploadItem.trim()]
                      }));
                      setNewFileUploadItem('');
                    }
                  }}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Add
                </button>
              </div>
              {form.fileUploadItems.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <span>- {item}</span>
                  <button
                    onClick={() => setForm(prev => ({
                      ...prev,
                      fileUploadItems: prev.fileUploadItems.filter((_, i) => i !== idx)
                    }))}
                    className="text-red-500 text-xs"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Company picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Companies * ({form.companies.length} selected)
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setForm(prev => ({ ...prev, companies: [...companyNames] }))}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Select all
                </button>
                <button
                  onClick={() => setForm(prev => ({ ...prev, companies: [] }))}
                  className="text-xs text-gray-500 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
            <input
              type="text"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2"
              placeholder="Search companies"
            />
            <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100">
              {visibleCompanies.length === 0 ? (
                <p className="text-sm text-gray-500 p-3">No companies match that search.</p>
              ) : (
                visibleCompanies.map(company => (
                  <label key={company.name} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.companies.includes(company.name)}
                      onChange={() => toggleCompany(company.name)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">{company.label}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {saving
                ? 'Saving...'
                : editingId
                  ? 'Save & Push to Companies'
                  : `Create Task for ${form.companies.length} ${form.companies.length === 1 ? 'Company' : 'Companies'}`}
            </button>
            <button
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing bulk tasks */}
      <div className="card p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Multi-Company Tasks</h4>
        {loading ? (
          <p className="text-sm text-gray-500">Loading tasks...</p>
        ) : bulkTasks.length === 0 ? (
          <p className="text-sm text-gray-500">No multi-company tasks yet.</p>
        ) : (
          <div className="space-y-3">
            {bulkTasks.map(bulkTask => (
              <div key={bulkTask.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{bulkTask.title}</p>
                    {bulkTask.description && (
                      <p className="text-sm text-gray-600 mt-1">{bulkTask.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                        {bulkTask.assignedTo === 'both' ? 'Admin & Client' : bulkTask.assignedTo === 'client' ? 'Client' : 'Admin'}
                      </span>
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                        {bulkTask.taskType === 'file-upload' ? 'File Upload' : 'Mark as Complete'}
                      </span>
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                        {describeSchedule(bulkTask)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      <span className="font-medium">{bulkTask.companies.length}</span>{' '}
                      {bulkTask.companies.length === 1 ? 'company' : 'companies'}: {bulkTask.companies.map(companyLabel).join(', ')}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEditForm(bulkTask)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(bulkTask)}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTaskManagement;
